import { Lightning } from "@lightningjs/sdk";
import { List } from "@lightningjs/ui";
import theme from "../../../../lib/theme";
import { ChannelModel, ProgramModel } from "../../../../lib/models";
import { ChannelItem } from "../ChannelItem/ChannelItem";
import { GroupTitle } from "../GroupTitle/GroupTitle";
import { getLiveTvChannels } from "../../../../api/queries";
import {MENU_WIDTH} from "../../../../lib/utils";

interface LiveTvGroup {
    name: string;
    channels: ChannelModel[];
}

interface ChannelListSpec extends Lightning.Component.TemplateSpec {
    Container: {
        List: typeof List;
    };
}

export class ChannelList
    extends Lightning.Component<ChannelListSpec>
    implements Lightning.Component.ImplementTemplateSpec<ChannelListSpec>
{
    private _channels: ChannelModel[] = [];
    private _groups: LiveTvGroup[] = [];

    static override _template(): Lightning.Component.Template<ChannelListSpec> {
        return {
            w: theme.layout.w - MENU_WIDTH,
            h: theme.layout.h,
            flex: { direction: "column" },
            collision: true,
            zIndex: 10,
            Container: {
                h: theme.layout.h,
                w: 617,
                x: 0,
                y: 0,
                clipping: true,
                collision: true,
                List: {
                    type: List,
                    collision: true,
                    direction: "column",
                    spacing: 24,
                    h: theme.layout.h,
                    w: 607,
                    y: 0,
                    x: 0,
                    scroll: { after: 2 },
                    gcThreshold: 24,
                    requestThreshold: 20,
                    enableRequests: true,
                    signals: { onRequestItems: true },
                    scrollTransition: {
                        duration: 0.3,
                        timingFunction: "cubic-bezier(0.4, 0, 0.2, 1)"
                    }
                }
            }
        };
    }

    readonly Container = this.getByRef("Container")!;
    readonly List = this.Container.getByRef("List")! as List;

    override _getFocused(): Lightning.Component | null | undefined {
        return this.List;
    }

    override _init() {
        // Override List navigation to skip group titles
        this.List._handleUp = this._handleUp.bind(this);
        this.List._handleDown = this._handleDown.bind(this);
    }

    override _handleUp() {
        const currentIndex = this.List.index;
        let newIndex = currentIndex - 1;

        // Find previous focusable item (skip group titles)
        while (newIndex >= 0 && this.List.items[newIndex]?.isGroupTitle) {
            newIndex--;
        }

        // If we reach the top → wrap to the last channel (symmetric with _handleDown)
        if (newIndex < 0) {
            for (let i = this.List.items.length - 1; i >= 0; i--) {
                if (!this.List.items[i]?.isGroupTitle) {
                    this.List.setIndex(i);
                    return true;
                }
            }
            return false;
        }

        this.List.setIndex(newIndex);
        return true;
    }

    override _handleDown() {
        const currentIndex = this.List.index;
        let newIndex = currentIndex + 1;

        // Skip over group titles
        while (newIndex < this.List.items.length && this.List.items[newIndex]?.isGroupTitle) {
            newIndex++;
        }

        // If we reach the end → wrap to the first channel
        if (newIndex >= this.List.items.length) {
            for (let i = 0; i < this.List.items.length; i++) {
                if (!this.List.items[i]?.isGroupTitle) {
                    this.List.setIndex(i);
                    return true;
                }
            }
            return false;
        }

        this.List.setIndex(newIndex);
        return true;
    }

    _calculateProgress(program: ProgramModel | undefined, now: Date): number {
        if (!program) return 0;

        const start = new Date(program.start);
        const stop = new Date(program.stop);
        const totalDuration = stop.getTime() - start.getTime();
        const elapsed = now.getTime() - start.getTime();
        return Math.round((elapsed / totalDuration) * 100);
    }

    _findNextProgram(programs: ProgramModel[], now: Date): ProgramModel | undefined {
        return programs.find((program) => {
            const start = new Date(program.start);
            return now < start;
        });
    }

    _findCurrentProgram(programs: ProgramModel[], now: Date): ProgramModel | undefined {
        return programs.find((program) => {
            const start = new Date(program.start);
            const stop = new Date(program.stop);
            return now >= start && now < stop;
        });
    }

    _createChannelItem = (channel: ChannelModel, index: number) => {
        const now = new Date();
        const currentProgram = channel.programs ? this._findCurrentProgram(channel.programs, now) : undefined;
        const nextProgram = channel.programs ? this._findNextProgram(channel.programs, now) : undefined;
        const progress = this._calculateProgress(currentProgram, now);

        return {
            type: ChannelItem,
            item: {
                ...channel,
                channelNumber: (index + 1).toString().padStart(3, "0"),
                currentProgram,
                nextProgram,
                progress
            }
        };
    };

    reload(channels: ChannelModel[]): void {
        this._channels = channels;
        const items = channels.map(this._createChannelItem);
        this.List.reload(items);
    }

    addItems(channels: ChannelModel[]): void {
        const startIndex = this._channels.length;
        this._channels = [...this._channels, ...channels];

        if (this.List.items?.length) {
            const newItems = channels.map((channel, index) => this._createChannelItem(channel, startIndex + index));
            this.List.add(newItems);
        } else {
            this.reload(channels);
        }
    }

    addGroups(groups: LiveTvGroup[]): void {
        this._groups = groups;
        this._channels = groups.flatMap(group => group.channels);
        
        const items = this._createGroupedItems(groups);
        this.List.reload(items);

        // Disable backend fetch for grouped mode
        this.List.enableRequests = false;
        
        // Set initial focus to first channel (skip group titles)
        this._setInitialFocus();
    }

    _setInitialFocus(): void {
        // Find first non-group-title item
        const firstChannelIndex = this.List.items.findIndex((item: any) => !item.isGroupTitle);
        if (firstChannelIndex !== -1) {
            this.List.setIndex(firstChannelIndex);
        }
    }

    _createGroupedItems(groups: LiveTvGroup[]): any[] {
        const items: any[] = [];
        let channelIndex = 0;

        groups.forEach(group => {
            // Add group title
            items.push({
                type: GroupTitle,
                title: group.name,
                isGroupTitle: true
            });

            // Add channels in this group
            group.channels.forEach(channel => {
                items.push(this._createChannelItem(channel, channelIndex));
                channelIndex++;
            });
        });

        return items;
    }

    async onRequestItems() {
        return (await getLiveTvChannels(this.List.items.length)).map((channel: ChannelModel, index: number) =>
            this._createChannelItem(channel, this.List.items.length + index)
        );
    }

    get items() {
        return this.List.items;
    }

    get currentItem() {
        return this.List.currentItem;
    }

    get index() {
        return this.List.index;
    }

    set index(value: number) {
        // Skip group titles when navigating
        const targetIndex = this._findNearestChannelIndex(value);
        if (targetIndex !== -1) {
            this.List.setIndex(targetIndex);
        }
    }

    _findNearestChannelIndex(targetIndex: number): number {
        // First check if the target index is already a channel
        if (targetIndex >= 0 && targetIndex < this.List.items.length) {
            const item = this.List.items[targetIndex];
            if (item && !item.isGroupTitle) {
                return targetIndex;
            }
        }

        // Find the nearest channel (prefer forward direction)
        const nextIndex = this._findNextChannelIndex(targetIndex);
        if (nextIndex !== -1) {
            return nextIndex;
        }

        // If no forward channel, try backward
        const prevIndex = this._findPreviousChannelIndex(targetIndex);
        if (prevIndex !== -1) {
            return prevIndex;
        }

        return -1;
    }

    _findNextChannelIndex(startIndex: number): number {
        for (let i = startIndex + 1; i < this.List.items.length; i++) {
            if (!this.List.items[i].isGroupTitle) {
                return i;
            }
        }
        return -1;
    }

    _findPreviousChannelIndex(startIndex: number): number {
        for (let i = startIndex - 1; i >= 0; i--) {
            if (!this.List.items[i].isGroupTitle) {
                return i;
            }
        }
        return -1;
    }
}
